import React, { useState } from 'react';
import { FileQuestion, Search } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQs = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      category: "For Students & Parents",
      icon: "📚",
      faqs: [
        {
          question: "How do I find a tutor?",
          answer: "You can find tutors by using our search feature on the homepage. Select your class/grade and area, then browse through verified tutor profiles. You can view their qualifications, experience, subjects taught, and reviews from other students."
        },
        {
          question: "How does the free demo class work?",
          answer: "Once you find a tutor you're interested in, you can book a free demo class directly through their profile. The demo class typically lasts 30-45 minutes and gives you a chance to experience their teaching style before committing to regular classes."
        },
        {
          question: "Are the quiz tests really free?",
          answer: "Yes! All quiz-based tests on PriEdu are currently 100% free for all registered users. You can access quizzes across multiple subjects and classes, track your progress, and review your performance without any payment."
        },
        {
          question: "How do I book a tutor after the demo class?",
          answer: "After your demo class, if you're satisfied with the tutor, you can directly discuss and finalize the schedule and fees with them. The booking and payment terms are agreed upon between you and the tutor."
        },
        {
          question: "What subjects are covered?",
          answer: "We have tutors for all major subjects including Mathematics, Science (Physics, Chemistry, Biology), English, Hindi, Social Studies, and more. Tutors are available for classes from primary school to higher secondary."
        },
        {
          question: "How are tutors verified?",
          answer: "All tutors on our platform go through a rigorous verification process that includes identity verification, educational qualification checks, background verification, and skill assessment tests. Only verified tutors can accept bookings."
        },
        {
          question: "What if I'm not satisfied with a tutor?",
          answer: "If you're not satisfied with a tutor after the demo class, there's no obligation to continue. You can simply book a demo with another tutor. We encourage students to try multiple tutors to find the best fit."
        },
        {
          question: "How do I contact support?",
          answer: "You can reach our support team by calling 94555 45273 or emailing support@priedu.com. We typically respond within 24-48 hours."
        }
      ]
    },
    {
      category: "For Tutors",
      icon: "👨‍🏫",
      faqs: [
        {
          question: "How do I register as a tutor?",
          answer: "Click on 'Become a Tutor' on our homepage and fill out the registration form with your personal details, educational qualifications, subjects you can teach, and preferred locations. After registration, you'll need to complete the verification process."
        },
        {
          question: "What is the verification process?",
          answer: "The verification process includes: 1) Identity verification using government ID, 2) Educational qualification verification, 3) Address verification, 4) Skill assessment test in your subject areas, and 5) Background check. The process typically takes 3-5 business days."
        },
        {
          question: "How much can I earn?",
          answer: "Tutors set their own fees based on their qualifications, experience, and subjects. Earnings depend on the number of students you teach and your hourly rate. Top tutors on our platform earn significant income through regular students."
        },
        {
          question: "How do I receive payments?",
          answer: "Payments are handled directly between you and the students/parents. You can choose your preferred payment method (cash, UPI, bank transfer, etc.). PriEdu may charge a small platform fee for successful student connections."
        },
        {
          question: "Can I set my own schedule?",
          answer: "Yes! You have complete flexibility to set your available time slots and locations. Students can only book demo classes during your specified available hours."
        },
        {
          question: "What are the requirements to become a tutor?",
          answer: "To become a tutor on PriEdu, you need: Minimum educational qualification of 12th pass (higher qualifications preferred), expertise in the subjects you want to teach, valid government ID proof, clean background record, and passion for teaching."
        }
      ]
    },
    {
      category: "General",
      icon: "❓",
      faqs: [
        {
          question: "What areas do you serve?",
          answer: "Currently, PriEdu serves the following areas in Uttar Pradesh: Lucknow, Sitapur, and Barabanki. We are continuously expanding to more cities. Contact us if you'd like us to add your area."
        },
        {
          question: "Is there a mobile app?",
          answer: "We are currently developing a mobile application that will offer enhanced features including study material delivery, offline quiz access, and more. The app will be available on Android and iOS. Stay tuned for updates!"
        },
        {
          question: "How do I contact customer support?",
          answer: "You can contact our customer support team via: Phone: 94555 45273 (Monday-Saturday, 9 AM - 7 PM), Email: support@priedu.com, or through the Contact Us page on our website."
        },
        {
          question: "What future features are planned?",
          answer: "We have exciting features planned including: Expanded quiz-based test series with detailed analytics, Mobile app for study material delivery, Video tutoring options, AI-powered tutor matching, and Performance tracking dashboards for parents."
        },
        {
          question: "Is my personal information safe?",
          answer: "Yes, we take data privacy very seriously. Please read our Privacy Policy for detailed information on how we collect, use, and protect your personal information. We never share your data with third parties without your consent."
        }
      ]
    }
  ];

  const filterFAQs = (faqs, query) => {
    if (!query) return faqs;
    return faqs.filter(faq => 
      faq.question.toLowerCase().includes(query.toLowerCase()) ||
      faq.answer.toLowerCase().includes(query.toLowerCase())
    );
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: filterFAQs(category.faqs, searchQuery)
  })).filter(category => category.faqs.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6 shadow-lg">
              <FileQuestion className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about PriEdu
            </p>
          </div>

          <div className="mb-12">
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 border-gray-200 bg-white shadow-sm"
              />
            </div>
          </div>

          {filteredCategories.length === 0 ? (
            <Card className="p-8 text-center bg-white shadow-lg">
              <p className="text-gray-600 text-lg">
                No questions found matching "{searchQuery}". Try a different search term or browse the categories below.
              </p>
            </Card>
          ) : (
            <div className="space-y-8">
              {filteredCategories.map((category, categoryIndex) => (
                <Card key={categoryIndex} className="p-6 bg-white shadow-lg border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-2xl">{category.icon}</span>
                    <h2 className="text-xl font-bold text-gray-900">
                      {category.category}
                    </h2>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {category.faqs.map((faq, faqIndex) => (
                      <AccordionItem 
                        key={faqIndex} 
                        value={`${categoryIndex}-${faqIndex}`}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-blue-600 py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Card>
              ))}
            </div>
          )}

          <Card className="mt-12 p-8 bg-gradient-to-br from-blue-600 to-teal-500 text-white text-center shadow-xl">
            <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
            <p className="text-blue-100 mb-6">
              Our support team is here to help you
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:9455545273" 
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Call: 94555 45273
              </a>
              <a 
                href="mailto:support@priedu.com"
                className="inline-flex items-center justify-center gap-2 bg-white/20 text-white border-2 border-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors"
              >
                Email Us
              </a>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default FAQs;
